import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/StatusBadge";

export default function PinDialog({ open, onOpenChange, image }) {
  const { user, login } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/collections/mine");
      setCollections(res.data.filter((c) => c.status === "draft" || c.status === "rejected"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user) load();
  }, [open, user]);

  const addToCollection = async (cid) => {
    setBusyId(cid);
    try {
      await api.post(`/collections/${cid}/items`, {
        archive_id: image.archive_id,
        title: image.title,
        image_url: image.image_url,
        link: image.link,
        source_collection: image.source_collection,
        date: image.date,
        format: image.format,
        caption: image.caption || "",
      });
      toast.success("Pinned to collection");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add image");
    } finally {
      setBusyId(null);
    }
  };

  const createAndAdd = async () => {
    if (!newTitle.trim()) return;
    setBusyId("new");
    try {
      const res = await api.post("/collections", { title: newTitle.trim() });
      await addToCollection(res.data.id);
      setNewTitle("");
    } catch (e) {
      toast.error("Could not create collection");
      setBusyId(null);
    }
  };

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="pin-dialog-signin">
          <DialogHeader>
            <DialogTitle className="font-serif">Sign in to curate</DialogTitle>
            <DialogDescription>Create your own collections of Nepal's history.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Create your own Pinterest-style collections of Nepal's history. Sign in to get started.
          </p>
          <button
            data-testid="pin-dialog-signin-btn"
            onClick={login}
            className="mt-2 bg-brick hover:bg-brick-dark text-parchment px-4 py-2.5 rounded-full text-sm font-semibold transition-colors"
          >
            Sign in with Google
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Add to collection</DialogTitle>
          <DialogDescription>Pin this image to one of your draft collections, or create a new one.</DialogDescription>
        </DialogHeader>

        {image?.image_url && (
          <div className="flex gap-3 items-center border border-stoneline rounded-lg p-2 bg-stone-50">
            <img src={image.image_url} alt="" className="h-14 w-14 rounded object-cover" />
            <p className="text-sm font-medium line-clamp-2">{image.title || "Untitled item"}</p>
          </div>
        )}

        <div className="flex gap-2 mt-1">
          <Input
            data-testid="new-collection-input"
            placeholder="New collection name…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createAndAdd()}
          />
          <button
            data-testid="create-and-add-btn"
            onClick={createAndAdd}
            disabled={!newTitle.trim() || busyId === "new"}
            className="shrink-0 inline-flex items-center gap-1.5 bg-brick hover:bg-brick-dark disabled:opacity-50 text-parchment px-3 rounded-md text-sm font-semibold"
          >
            {busyId === "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-1.5">
          {loading && <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>}
          {!loading && collections.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No editable collections yet — create one above.
            </p>
          )}
          {collections.map((c) => (
            <button
              key={c.id}
              data-testid={`pin-to-${c.id}`}
              onClick={() => addToCollection(c.id)}
              disabled={busyId === c.id || c.items.length >= 25}
              className="w-full flex items-center justify-between gap-3 p-2 rounded-lg border border-stoneline hover:border-brick hover:bg-stone-50 transition-colors text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-3 min-w-0">
                {c.cover_image ? (
                  <img src={c.cover_image} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                ) : (
                  <span className="h-10 w-10 rounded bg-stone-200 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.title}</div>
                  <div className="text-xs text-muted-foreground">{c.items.length}/25 images</div>
                </div>
              </div>
              {busyId === c.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-brick" />
              ) : (
                <span className="text-xs font-semibold text-brick flex items-center gap-1"><Check className="h-4 w-4" /></span>
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
